import ScrollTableGuide from '@/components/general/ScrollTableGuide';
import { DURATION_TIME_TYPES } from '@/configs/constants';
import { Capacitie } from '@/types/capacitie';
import { PriceDetails } from '@/types/price';
import { formatToCurrency } from '@/utils/formatNumber';
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const PriceSection: React.FC<{
    item: PriceDetails,
    capacities: Capacitie[] | undefined
}> = ({ item, capacities }) => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScrollable = () => setShowHint(el.scrollWidth > el.clientWidth);
        requestAnimationFrame(checkScrollable);

        const handleScroll = () => {
            if (el.scrollLeft > 0 && onFirstScroll.current) {
                onFirstScroll.current = false;
                setShowHint(false);
            }
        };
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [item.priceDetails, capacities]);

    return (
        <div>
            <div className='border border-border-gray2 relative'>
                <div className='flex items-center justify-center bg-primaryWeb min-h-[50px]'>
                    <p className='font-bold text-white'>{item.name}</p>
                </div>
                <ScrollTableGuide showHint={showHint && onFirstScroll.current} />

                <div className='max-w-full overflow-auto relative' ref={scrollRef}>
                    <div className='flex sm:grid grid-cols-5 bg-[#F8F7F1] divide-x divide-y divide-border-gray2 sm:min-h-[60px] min-h-[40px]'>
                        <div className='bg-[#F8F7F1] common-item font-medium flex items-center justify-end h-full'>
                            {t('words_title.engine_size')}
                        </div>
                        {Object.values(DURATION_TIME_TYPES).map(time => (
                            <div key={time.key} className='bg-[#F8F7F1] common-item flex font-medium items-center justify-center h-full'>
                                {t(`${time.key}`)}
                            </div>
                        ))}
                    </div>

                    {capacities && capacities.map(capacity => (
                        <div key={capacity.id} className='flex sm:grid grid-cols-5 divide-x divide-y divide-border-gray2'>
                            <div className=' flex flex-col break-words items-end font-medium text-right common-item bg-[#F8F7F1] border-t border-border-gray2'>
                                {t(`price_list.${capacity.code}`)}
                                {['moped', 'new_standard_moped'].includes(capacity.code) && <span className='text-primaryWeb text-fs-mid12'>要原付免許</span>}
                                {['moped_class_2'].includes(capacity.code) && <span className='text-primaryWeb text-fs-mid12'>要小型二輪免許</span>}
                            </div>
                            {Object.values(DURATION_TIME_TYPES).map(duration => (
                                <div key={duration.value} className='common-item flex justify-center items-center break-words'>
                                    {formatToCurrency(item.priceDetails.find(e => e.capacitieId === capacity.id && e.time === duration.value)?.price ?? '0') + t('currency.currency_unit')}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <p className='text-fs-mid12 xsm:text-fs-mid13 xsm:leading-[24px] leading-[20px] mt-[10px] text-center text-black-6'>※価格は全て税込みです</p>
        </div>
    );
};
